/**
 * API Service
 * Il Diario di Bordo - Frontend
 *
 * MIGRAZIONE httpOnly Cookie:
 * - I token JWT sono gestiti via cookie httpOnly (impostati dal backend)
 * - Il frontend NON legge/scrive piu' token in localStorage
 * - CSRF token inviato come header X-CSRF-Token
 * - withCredentials: true per inviare cookie cross-origin
 */

import axios from 'axios'
import { getCsrfToken } from '../utils/csrf'

// Base URL esplicita via env var (più affidabile di import.meta.env.PROD,
// che su alcune build di Vercel può risolvere a false e produrre baseURL relativa).
// VITE_API_BASE_URL va settata negli Environment Variables di Vercel.
// In dev locale, fallback a '/api' (proxy Vite verso il backend Express su :3001).
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api'

// Crea istanza Axios
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json'
  },
  // Cookie httpOnly inviati automaticamente dal browser
  withCredentials: true
})

// Request interceptor - aggiunge CSRF token
api.interceptors.request.use(
  (config) => {
    // Aggiungi CSRF token per richieste mutanti
    if (['post', 'put', 'delete', 'patch'].includes(config.method)) {
      const csrfToken = getCsrfToken()
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken
      }
    }

    return config
  },
  (error) => Promise.reject(error)
)

// Response interceptor - gestisce refresh token, manutenzione e 2FA
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config

    // Gestione MAINTENANCE MODE (503)
    if (error.response?.status === 503 && error.response?.data?.code === 'MAINTENANCE_MODE') {
      const maintenanceMessage = error.response.data.message || 'Il sistema è in manutenzione. Torneremo online a breve.'

      localStorage.setItem('maintenanceMode', 'true')
      localStorage.setItem('maintenanceMessage', maintenanceMessage)

      window.dispatchEvent(new CustomEvent('maintenance-mode', {
        detail: { message: maintenanceMessage }
      }))

      return Promise.reject(error)
    }

    // Gestione 2FA_SETUP_REQUIRED (403)
    if (error.response?.status === 403 && error.response?.data?.code === '2FA_SETUP_REQUIRED') {
      window.dispatchEvent(new CustomEvent('2fa-setup-required', {
        detail: {
          message: error.response.data.message,
          redirectTo: error.response.data.data?.redirectTo || '/setup-2fa'
        }
      }))

      if (!window.location.pathname.includes('/setup-2fa') &&
          !window.location.pathname.includes('/auth/2fa')) {
        window.location.href = '/setup-2fa'
      }

      return Promise.reject(error)
    }

    // Se 401 e non e' gia' un retry
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true

      // Non fare refresh se siamo su pagine auth
      const currentPath = window.location.pathname
      if (currentPath === '/login' || currentPath === '/register' ||
          currentPath === '/forgot-password' || currentPath === '/reset-password' ||
          currentPath === '/verify-2fa' || currentPath === '/accept-invite') {
        return Promise.reject(error)
      }

      try {
        // Il refresh token e' nel cookie httpOnly — il browser lo invia automaticamente
        console.log('[API] Attempting token refresh via cookie')
        await axios.post(
          `${API_BASE_URL}/auth/refresh`,
          {}, // Body vuoto — il refresh token e' nel cookie
          { withCredentials: true }
        )

        console.log('[API] Token refresh successful')
        // Il backend ha impostato nuovi cookie — riprova la richiesta originale
        return api(originalRequest)
      } catch (refreshError) {
        console.error('[API] Token refresh failed:', refreshError.message)

        // Redirect solo se non siamo su pagine pubbliche
        const publicPaths = ['/login', '/register', '/forgot-password', '/reset-password', '/verify-2fa', '/accept-invite', '/verify-email', '/resend-verification']
        if (!publicPaths.includes(currentPath)) {
          window.location.href = '/login'
        }

        return Promise.reject(refreshError)
      }
    }

    return Promise.reject(error)
  }
)

export default api

// === API HELPERS ===

// Auth
export const authApi = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  me: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  refresh: () => api.post('/auth/refresh'), // Body vuoto, refresh token nel cookie
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', { currentPassword, newPassword }),
  updatePreferences: (features) => api.put('/auth/preferences', { features })
}

// 2FA Setup (AUTH required - /api/auth/2fa)
export const twoFactorApi = {
  // Status
  getStatus: () => api.get('/auth/2fa/status'),

  // TOTP
  setupTotp: () => api.post('/auth/2fa/totp/setup'),
  verifyTotp: (code) => api.post('/auth/2fa/totp/verify', { code }),
  disableTotp: (code) => api.post('/auth/2fa/totp/disable', { code }),

  // Email OTP
  enableEmail: () => api.post('/auth/2fa/email/enable'),
  disableEmail: () => api.post('/auth/2fa/email/disable'),

  // Backup codes
  regenerateBackupCodes: () => api.post('/auth/2fa/backup-codes/regenerate'),
  downloadBackupCodesPdf: (codes) => api.post('/auth/2fa/backup-codes/download-pdf', { codes }, { responseType: 'arraybuffer' }),

  // WebAuthn/Passkey - Setup (AUTH required)
  webauthnRegisterOptions: () => api.post('/auth/2fa/webauthn/register-options'),
  webauthnRegisterVerify: (data) => api.post('/auth/2fa/webauthn/register-verify', data),
  webauthnDeleteCredential: (credentialId) => api.delete(`/auth/2fa/webauthn/credentials/${credentialId}`)
}

// 2FA Login (NO AUTH - usa twoFactorToken - /api/auth/2fa-login)
export const twoFactorLoginApi = {
  // Check trusted device
  checkTrusted: (twoFactorToken, deviceToken = null) =>
    api.post('/auth/2fa-login/check-trusted', { twoFactorToken, deviceToken }),

  // Verify code (TOTP, Email, Backup)
  verify: (twoFactorToken, code, method, rememberDevice = false) =>
    api.post('/auth/2fa-login/verify', { twoFactorToken, code, method, rememberDevice }),

  // Send email code
  sendCode: (twoFactorToken) =>
    api.post('/auth/2fa-login/send-code', { twoFactorToken }),

  // WebAuthn/Passkey - Login (NO AUTH)
  webauthnAuthOptions: (twoFactorToken) =>
    api.post('/auth/2fa-login/webauthn/auth-options', { twoFactorToken }),

  webauthnVerify: (twoFactorToken, webauthnResponse, rememberDevice = false) =>
    api.post('/auth/2fa-login/verify', {
      twoFactorToken,
      method: 'webauthn',
      webauthnResponse,
      rememberDevice
    }),

  // Rate limit status (dev only)
  getRateLimitStatus: (userId) => api.get(`/auth/2fa-login/rate-limit-status/${userId}`)
}

// Trusted Devices (FASE 5: Remember Device + Email Suggest)
export const trustedDevicesApi = {
  list: () => api.get('/auth/devices'),
  check: () => api.get('/auth/devices/check'),
  trust: (name) => api.post('/auth/devices/trust', { name }),
  revoke: (id) => api.delete(`/auth/devices/${id}`),
  revokeAll: () => api.delete('/auth/devices'),
  rename: (id, name) => api.put(`/auth/devices/${id}/rename`, { name }),
  hasRemembered: () => api.get('/auth/devices/has-remembered'),
  emailSuggest: (prefix) => api.post('/auth/devices/email-suggest', { prefix })
}

// Minori
export const minoriApi = {
  list: (params) => api.get('/minori', { params }),
  get: (id) => api.get(`/minori/${id}`),
  create: (data) => api.post('/minori', data),
  update: (id, data) => api.put(`/minori/${id}`, data),
  delete: (id) => api.delete(`/minori/${id}`),
  archive: (id) => api.post(`/minori/${id}/archive`),
  restore: (id) => api.post(`/minori/${id}/restore`),
  stats: () => api.get('/minori/stats'),
  // Prep Colloquio (Fase 8)
  prepColloquio: (id, params) => api.get(`/minori/${id}/prep-colloquio`, { params })
}

// Entries
export const entriesApi = {
  list: (params) => api.get('/entries', { params }),
  get: (id) => api.get(`/entries/${id}`),
  create: (data) => api.post('/entries', data),
  update: (id, data) => api.put(`/entries/${id}`, data),
  delete: (id, deleteReason) => api.delete(`/entries/${id}`, { data: { deleteReason } }),
  getByMinore: (minoreId, params) => api.get(`/entries/minore/${minoreId}`, { params }),
  getTodayByMinore: (minoreId) => api.get(`/entries/minore/${minoreId}/today`),
  // Quick Check-in (Fase 1)
  bulkCheckin: (data) => api.post('/entries/bulk-checkin', data),
  getCheckin: (minoreId, params) => api.get(`/entries/checkin/${minoreId}`, { params }),
  getCheckinsToday: () => api.get('/entries/checkins-today'),
  // Attività di Gruppo (Fase 4)
  groupActivity: (data) => api.post('/entries/group-activity', data),
  getPendingActivities: (minoreId, params) => api.get(`/entries/pending-activities/${minoreId}`, { params })
}

// Trash (Cestino)
export const trashApi = {
  list: (params) => api.get('/entries/trash/list', { params }),
  stats: () => api.get('/entries/trash/stats'),
  activity: (limitOrParams) => {
    const params = typeof limitOrParams === 'number'
      ? { limit: limitOrParams }
      : limitOrParams
    return api.get('/entries/trash/activity', { params })
  },
  auditLog: (limitOrParams) => {
    const params = typeof limitOrParams === 'number'
      ? { limit: limitOrParams }
      : limitOrParams
    return api.get('/entries/trash/audit-log', { params })
  },
  checkRestore: (id) => api.post(`/entries/${id}/restore/check`),
  restore: (id, forceRestore = false) => api.post(`/entries/${id}/restore`, { forceRestore }),
  merge: (sourceId, targetId, selections) => api.post(`/entries/${sourceId}/merge`, {
    targetId,
    selections
  }),
  bulkRestore: (ids, skipDuplicates = false) => api.post('/entries/trash/bulk-restore', {
    ids,
    skipDuplicates
  }),
  deletePermanent: (id) => api.delete(`/entries/${id}/permanent`),
  bulkDelete: (ids) => api.delete('/entries/trash/bulk-delete', { data: { ids } }),
  empty: (confirmText) => api.post('/entries/trash/empty', { confirmText }),
  count: () => api.get('/entries/trash/stats')
}

// Dashboard Stats
export const statsApi = {
  getDashboard: () => api.get('/stats/dashboard'),
  dashboard: () => api.get('/stats/dashboard'),
  getActivity: (params) => api.get('/stats/activity', { params })
}

// Users
export const usersApi = {
  list: (params) => api.get('/users', { params }),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  toggleActive: (id) => api.post(`/users/${id}/toggle-active`),
  invite: (data) => api.post('/users/invite', data),
  resendInvite: (id) => api.post(`/users/${id}/resend-invite`)
}

// Settings
export const settingsApi = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
  updateBranding: (data) => api.put('/settings/branding', data),
  uploadLogo: (file) => {
    const formData = new FormData()
    formData.append('logo', file)
    return api.post('/settings/logo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
  },
  getEntryOptions: () => api.get('/settings/entry-options'),
  updateEntryOptions: (data) => api.put('/settings/entry-options', data),
  getCustomSections: () => api.get('/settings/custom-sections'),
  updateCustomSections: (data) => api.put('/settings/custom-sections', data),
  getEnabledFields: () => api.get('/settings/enabled-fields'),
  updateEnabledFields: (data) => api.put('/settings/enabled-fields', data)
}

// Google Drive
export const googleDriveApi = {
  getStatus: () => api.get('/gdrive/status'),
  startAuth: () => api.get('/gdrive/auth-url'),
  disconnect: () => api.post('/gdrive/disconnect'),
  listBackups: () => api.get('/gdrive/backups'),
  createBackup: () => api.post('/gdrive/backup'),
  restoreBackup: (fileId) => api.post('/gdrive/restore', { fileId }),
  updateSettings: (data) => api.put('/gdrive/settings', data),
  status: () => api.get('/gdrive/status'),
  getAuthUrl: () => api.get('/gdrive/auth-url'),
  backup: () => api.post('/gdrive/backup'),
  restore: (data) => api.post('/gdrive/restore', data),
  analyze: (fileId) => api.post(`/gdrive/analyze/${fileId}`),
  deleteBackup: (fileId) => api.delete(`/gdrive/backup/${fileId}`)
}

export const gdriveApi = googleDriveApi

// Calendar
export const calendarApi = {
  getEvents: (params) => api.get('/calendario/events', { params }),
  createEvent: (data) => api.post('/calendario/events', data),
  updateEvent: (id, data) => api.put(`/calendario/events/${id}`, data),
  deleteEvent: (id) => api.delete(`/calendario/events/${id}`),
  getHolidays: (year) => api.get(`/calendario/festivita/${year}`),
  getSettings: () => api.get('/calendario/settings'),
  updateSettings: (data) => api.put('/calendario/impostazioni', data),
  getProssimeChiusure: (giorniOrParams) => {
    const params = typeof giorniOrParams === 'number'
      ? { giorni: giorniOrParams }
      : giorniOrParams
    return api.get('/calendario/prossime-chiusure', { params })
  },
  getStatoGiorno: (data) => api.get(`/calendario/stato/${data}`),
  getMese: (anno, mese) => api.get(`/calendario/mese/${anno}/${mese}`),
  addChiusura: (data) => api.post('/calendario/chiusure', data),
  deleteChiusura: (id) => api.delete(`/calendario/chiusure/${id}`),
  addAperturaEccezione: (data) => api.post('/calendario/aperture-eccezioni', data),
  deleteAperturaEccezione: (id) => api.delete(`/calendario/aperture-eccezioni/${id}`),
  toggleFestivita: (codice, data) => api.put(`/calendario/festivita/${codice}`, data),
  updateSantoPatrono: (data) => api.put('/calendario/santo-patrono', data),
  getIcalToken: () => api.get('/calendario/ical-token'),
  regenerateIcalToken: () => api.post('/calendario/ical-token'),
  updateImpostazioni: (data) => api.put('/calendario/impostazioni', data),
  removeChiusura: (id) => api.delete(`/calendario/chiusure/${id}`),
  removeAperturaEccezione: (id) => api.delete(`/calendario/aperture-eccezioni/${id}`),
  updateFestivita: (codice, data) => api.put(`/calendario/festivita/${codice}`, data)
}

export const calendarioApi = calendarApi

// Announcements
export const announcementsApi = {
  getPublic: () => api.get('/public/announcements'),
  list: () => api.get('/admin/announcements'),
  create: (data) => api.post('/admin/announcements', data),
  update: (id, data) => api.put(`/admin/announcements/${id}`, data),
  delete: (id) => api.delete(`/admin/announcements/${id}`)
}

// Reports
export const reportsApi = {
  getTemplates: () => api.get('/reports/templates'),
  getTemplate: (id) => api.get(`/reports/templates/${id}`),
  createTemplate: (data) => api.post('/reports/templates', data),
  updateTemplate: (id, data) => api.put(`/reports/templates/${id}`, data),
  deleteTemplate: (id) => api.delete(`/reports/templates/${id}`),
  duplicateTemplate: (id) => api.post(`/reports/templates/${id}/duplicate`),
  getMinoriList: () => api.get('/reports/minori'),
  getCompilationData: (targetType, targetId, startDate, endDate) =>
    api.get(`/reports/generate/data/${targetType}/${targetId}`, {
      params: { startDate, endDate }
    }),
  generatePreview: (data) => api.post('/reports/generate/preview', data),
  generatePdf: (data) => api.post('/reports/generate/pdf', data, { responseType: 'blob' }),
  generate: (data) => api.post('/reports/generate', data, { responseType: 'blob' }),
  preview: (data) => api.post('/reports/preview', data),
  downloadBlob: (blob, filename = 'report.pdf') => {
    const url = window.URL.createObjectURL(new Blob([blob]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', filename)
    document.body.appendChild(link)
    link.click()
    link.parentNode.removeChild(link)
    window.URL.revokeObjectURL(url)
  }
}

// Subscriptions
export const subscriptionsApi = {
  getCurrent: () => api.get('/subscriptions/current'),
  getPlans: () => api.get('/subscriptions/plans'),
  getInvoices: () => api.get('/subscriptions/invoices'),
  createCheckout: (data) => api.post('/subscriptions/checkout', data),
  openPortal: () => api.post('/subscriptions/portal'),
  cancel: (reason) => api.post('/subscriptions/cancel', { reason }),
  reactivate: () => api.post('/subscriptions/reactivate'),
  validateCoupon: (code) => api.post('/subscriptions/validate-coupon', { code })
}

// Invoices
export const invoicesApi = {
  getInvoices: (params) => api.get('/invoices', { params }),
  getInvoice: (id) => api.get(`/invoices/${id}`),
  downloadPdf: (id) => api.get(`/invoices/${id}/download`, { responseType: 'blob' }),
  resendEmail: (id) => api.post(`/invoices/${id}/resend-email`),
  downloadAndSave: async (invoiceId, invoiceNumber) => {
    const response = await api.get(`/invoices/${invoiceId}/download`, { responseType: 'blob' })
    const url = window.URL.createObjectURL(new Blob([response.data]))
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', `Fattura_${invoiceNumber || invoiceId}.pdf`)
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }
}

// Security
export const securityApi = {
  getSessions: () => api.get('/auth/security/sessions'),
  getActivity: (params) => api.get('/auth/security/activity', { params }),
  logoutAll: () => api.post('/auth/security/logout-all'),
  logoutOthers: () => api.post('/auth/security/logout-others')
}

// Organization APIs
export const organizationOnboardingApi = {
  register: (data) => api.post('/onboarding/organization/register', data),
  verifyEmail: (token) => api.post('/onboarding/organization/verify-email', { token }),
  resendVerification: (email) => api.post('/onboarding/organization/resend-verification', { email }),
  getStatus: () => api.get('/onboarding/organization/status'),
  completeFiscal: (data) => api.post('/onboarding/organization/complete-fiscal', data),
  completeAddress: (data) => api.post('/onboarding/organization/complete-address', data),
  acceptContracts: (documentIds) => api.post('/onboarding/organization/accept-contracts', { documentIds }),
  createFirstCenter: (data) => api.post('/onboarding/organization/create-first-center', data),
  getPendingContracts: () => api.get('/onboarding/organization/pending-contracts')
}

export const organizationApi = {
  getCurrent: () => api.get('/organization/current'),
  update: (data) => api.put('/organization/current', data),
  getStats: () => api.get('/organization/stats'),
  getCenters: (params) => api.get('/organization/centers', { params }),
  getCenter: (id) => api.get(`/organization/centers/${id}`),
  createCenter: (data) => api.post('/organization/centers', data),
  updateCenter: (id, data) => api.put(`/organization/centers/${id}`, data),
  suspendCenter: (id, reason) => api.post(`/organization/centers/${id}/suspend`, { reason }),
  reactivateCenter: (id) => api.post(`/organization/centers/${id}/reactivate`),
  requestCenterDeletion: (id, reason) => api.post(`/organization/centers/${id}/request-deletion`, { reason }),
  cancelCenterDeletion: (id) => api.post(`/organization/centers/${id}/cancel-deletion`),
  getCenterCoordinators: (centerId) => api.get(`/organization/centers/${centerId}/coordinators`),
  inviteCoordinator: (centerId, data) => api.post(`/organization/centers/${centerId}/coordinators/invite`, data),
  removeCoordinator: (centerId, userId) => api.delete(`/organization/centers/${centerId}/coordinators/${userId}`),
  getContracts: () => api.get('/organization/contracts'),
  acceptContract: (documentId) => api.post('/organization/contracts/accept', { documentId }),
  getFiscal: () => api.get('/organization/fiscal'),
  updateFiscal: (data) => api.put('/organization/fiscal', data),
  getBilling: () => api.get('/organization/billing'),
  getInvoices: (params) => api.get('/organization/invoices', { params }),
  getPaymentMethods: () => api.get('/organization/payment-methods'),
  requestDeletion: (reason) => api.post('/organization/request-deletion', { reason }),
  cancelDeletion: () => api.post('/organization/cancel-deletion'),
  getDeletionStatus: () => api.get('/organization/deletion-status'),
  requestDataExport: () => api.post('/organization/export-data'),
  getExports: () => api.get('/organization/exports'),
  downloadExport: (id) => api.get(`/organization/exports/${id}/download`, { responseType: 'blob' })
}

export const legalDocumentsApi = {
  getDocuments: () => api.get('/legal/documents'),
  getDocument: (id) => api.get(`/legal/documents/${id}`),
  getDocumentByType: (type) => api.get(`/legal/documents/type/${type}`),
  getPending: () => api.get('/legal/pending'),
  acknowledge: (id) => api.post(`/legal/acknowledge/${id}`),
  getAcknowledgments: () => api.get('/legal/acknowledgments')
}

// PEP
export const pepApi = {
  getAttivo: (minoreId) => api.get(`/pep/minore/${minoreId}`),
  getStorico: (minoreId) => api.get(`/pep/minore/${minoreId}/storico`),
  create: (data) => api.post('/pep', data),
  update: (id, data) => api.put(`/pep/${id}`, data),
  remove: (id) => api.delete(`/pep/${id}`),
  addObiettivo: (pepId, data) => api.post(`/pep/${pepId}/obiettivi`, data),
  updateObiettivo: (pepId, obId, data) => api.put(`/pep/${pepId}/obiettivi/${obId}`, data),
  removeObiettivo: (pepId, obId) => api.delete(`/pep/${pepId}/obiettivi/${obId}`),
  addVerifica: (pepId, obId, data) => api.post(`/pep/${pepId}/obiettivi/${obId}/verifiche`, data),
  addNota: (pepId, obId, data) => api.post(`/pep/${pepId}/obiettivi/${obId}/note`, data),
  addColloquio: (pepId, data) => api.post(`/pep/${pepId}/colloqui`, data),
  updateColloquio: (pepId, collId, data) => api.put(`/pep/${pepId}/colloqui/${collId}`, data),
  removeColloquio: (pepId, collId) => api.delete(`/pep/${pepId}/colloqui/${collId}`),
  addEvento: (pepId, data) => api.post(`/pep/${pepId}/eventi`, data),
  updateEvento: (pepId, evId, data) => api.put(`/pep/${pepId}/eventi/${evId}`, data),
  removeEvento: (pepId, evId) => api.delete(`/pep/${pepId}/eventi/${evId}`),
  getDashboard: () => api.get('/pep/dashboard'),
  getVerificheScadenza: (giorni = 14) => api.get(`/pep/verifiche-scadenza?giorni=${giorni}`),
  getTimeline: (pepId) => api.get(`/pep/${pepId}/timeline`),
  exportPdf: (pepId, data) => api.post(`/pep/${pepId}/export/pdf`, data, { responseType: 'blob' }),
  chiudi: (pepId, data) => api.post(`/pep/${pepId}/chiudi`, data, data.generaPdf ? { responseType: 'blob' } : {}),
  riapri: (pepId, data) => api.post(`/pep/${pepId}/riapri`, data),
  importRapido: (data) => api.post('/pep/import-rapido', data)
}

// ── Smart Alerts ──────────────────

export const alertsApi = {
  list: (params) => api.get('/alerts', { params }),
  count: () => api.get('/alerts/count'),
  markRead: (id) => api.put(`/alerts/${id}/read`),
  dismiss: (id) => api.put(`/alerts/${id}/dismiss`)
}

// ── Centro Notifiche ──────────────────

export const notificationsApi = {
  list: (params) => api.get('/notifications', { params }),
  count: () => api.get('/notifications/count'),
  markRead: (id) => api.put(`/notifications/${id}/read`),
  dismiss: (id) => api.put(`/notifications/${id}/dismiss`),
  markAllRead: () => api.put('/notifications/read-all')
}

export const notificationPreferencesApi = {
  get: () => api.get('/notification-preferences'),
  patch: (data) => api.patch('/notification-preferences', data)
}

export const pushApi = {
  getPublicKey: () => api.get('/push-subscriptions/public-key'),
  listDevices: () => api.get('/push-subscriptions'),
  subscribe: (data) => api.post('/push-subscriptions', data),
  unsubscribe: (endpoint) => api.delete('/push-subscriptions', { data: { endpoint } }),
  test: () => api.post('/push-subscriptions/test')
}

// ── Chat ──────────────────

export const chatApi = {
  listThreads: (params) => api.get('/chat/threads', { params }),
  createDirect: (otherUserId) => api.post('/chat/threads/direct', { otherUserId }),
  createGroup: ({ name, participantIds }) =>
    api.post('/chat/threads/channel', { name, participantIds }),
  archiveThread: (threadId) => api.put(`/chat/threads/${threadId}/archive`),
  listMessages: (threadId, params) => api.get(`/chat/threads/${threadId}/messages`, { params }),
  sendMessage: (threadId, body, mentions = []) =>
    api.post(`/chat/threads/${threadId}/messages`, { body, mentions }),
  editMessage: (messageId, body) => api.put(`/chat/messages/${messageId}`, { body }),
  deleteMessage: (messageId) => api.delete(`/chat/messages/${messageId}`),
  markThreadRead: (threadId, lastReadMessageId) =>
    api.put(`/chat/threads/${threadId}/read`, { lastReadMessageId }),
  listReactions: (messageId) => api.get(`/chat/messages/${messageId}/reactions`),
  addReaction: (messageId, emoji) => api.post(`/chat/messages/${messageId}/reactions`, { emoji }),
  removeReaction: (messageId, emoji) =>
    api.delete(`/chat/messages/${messageId}/reactions`, { data: { emoji } })
}

// ── Reminder ──────────────────

export const remindersApi = {
  list: (params) => api.get('/reminders', { params }),
  get: (id) => api.get(`/reminders/${id}`),
  create: (data) => api.post('/reminders', data),
  update: (id, data) => api.put(`/reminders/${id}`, data),
  complete: (id) => api.post(`/reminders/${id}/complete`),
  remove: (id) => api.delete(`/reminders/${id}`)
}
